import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { blogFAQs } from '../data/faqs';
import Icon from '../components/AppIcon';
import mermaid from 'mermaid';
import { sendContactFormEmail } from '../utils/emailService';

// Component to render Mermaid diagram in modal
const MermaidModalRenderer = ({ diagram, zoom }) => {
  const containerRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [svg, setSvg] = useState(null);

  useEffect(() => {
    if (containerRef.current && diagram && !rendered && mermaid) {
      const id = `mermaid-modal-${Date.now()}`;
      mermaid.render(id, diagram).then(({ svg: renderedSvg }) => {
        if (containerRef.current) {
          setSvg(renderedSvg);
          setRendered(true);
        }
      }).catch((error) => {
        console.error('Mermaid modal rendering error:', error);
        if (containerRef.current) {
          containerRef.current.innerHTML = '<div style="color: #ef4444; padding: 20px;">Error rendering diagram</div>';
        }
      });
    }
  }, [diagram, rendered]);

  return (
    <div style={{ 
      textAlign: 'center', 
      color: '#64748B',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {!rendered && <div style={{ marginBottom: '16px' }}>Rendering diagram...</div>}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          overflow: 'auto',
          padding: '20px'
        }}
      >
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            justifyContent: 'center',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease',
            maxWidth: '100%',
            maxHeight: '100%'
          }}
          dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
        />
      </div>
    </div>
  );
};

const BlogListing = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isIndividualBlog, setIsIndividualBlog] = useState(false);
  const [isTagFilter, setIsTagFilter] = useState(false);
  const [filteredTag, setFilteredTag] = useState(null); // For backward compatibility, keep the best match
  const [filteredTags, setFilteredTags] = useState([]); // All matching tags
  const [previousTagFilter, setPreviousTagFilter] = useState(null); // Track previous tag filter for back button
  const [allBlogs, setAllBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const mermaidInitialized = useRef(false);
  const [mermaidModal, setMermaidModal] = useState({ isOpen: false, diagram: null, title: '', svg: null });
  const [mermaidZoom, setMermaidZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactSubmitMessage, setContactSubmitMessage] = useState('');

  // Progressive blog loading using Vite's import.meta.glob
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setIsLoading(true);
        setLoadedCount(0);
        setAllBlogs([]); // Clear previous blogs
        
        // Use Vite's import.meta.glob to dynamically import all blog files
        const blogModules = import.meta.glob('../blogs/*.jsx');
        const blogPaths = Object.keys(blogModules);
        setTotalBlogs(blogPaths.length);
        
        if (blogPaths.length === 0) {
          setIsLoading(false);
          return;
        }
        
        // Load all blog files in parallel for faster loading
        // Use a Set to track loaded blog IDs to avoid duplicates
        const loadedBlogIds = new Set();
        let completedCount = 0;
        
        // Process each blog as it loads (progressive loading)
        const processBlog = (blogData) => {
          if (blogData && blogData.slug && !loadedBlogIds.has(blogData.slug)) {
            loadedBlogIds.add(blogData.slug);
            completedCount++;
            setLoadedCount(completedCount);
            
            // Update state with new blog, maintaining sorted order
            setAllBlogs(prevBlogs => {
              const updated = [...prevBlogs, blogData];
              // Sort by publish date (newest first)
              updated.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
              return updated;
            });
          } else {
            completedCount++;
            setLoadedCount(completedCount);
          }
        };
        
        // Start all blog loads in parallel and process as they complete
        const blogPromises = blogPaths.map(async (path) => {
          try {
            const module = await blogModules[path]();
            // Get the exported blog object (assuming each file exports one blog object)
            const blogData = Object.values(module)[0];
            if (blogData && blogData.slug && blogData.title) {
              processBlog(blogData);
              return blogData;
            } else {
              console.warn(`Invalid blog data in ${path}:`, blogData);
              processBlog(null);
              return null;
            }
          } catch (error) {
            console.error(`Error loading blog from ${path}:`, error);
            processBlog(null);
            return null;
          }
        });
        
        // Wait for all blogs to complete (for final state consistency)
        const allBlogsData = (await Promise.all(blogPromises)).filter(blog => blog !== null);
        
        // Final sort to ensure correct order (blogs may have loaded out of order)
        allBlogsData.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
        setAllBlogs(allBlogsData);
        setLoadedCount(allBlogsData.length);
      } catch (error) {
        console.error('Error loading blogs:', error);
        setAllBlogs([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBlogs();
  }, []);

  // Initialize Mermaid
  useEffect(() => {
    if (!mermaidInitialized.current) {
      mermaid.initialize({ 
        startOnLoad: false, // We'll manually trigger rendering
        theme: 'default',
        securityLevel: 'loose',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis'
        }
      });
      mermaidInitialized.current = true;
    }
  }, []);

  // Process Mermaid diagrams when blog content is displayed using HTML-based rendering
  useEffect(() => {
    if (!isIndividualBlog || !selectedBlog || !mermaidInitialized.current) {
      return;
    }

    let observer = null;
    let timeoutId = null;


    const processMermaidDiagrams = () => {
      // Process all .mermaid elements that haven't been processed yet
      const mermaidElements = document.querySelectorAll('.mermaid:not([data-processed])');
      
      if (mermaidElements.length > 0) {
        // console.log(`Found ${mermaidElements.length} unprocessed Mermaid diagram(s)`);
        
        mermaidElements.forEach((element, index) => {
          if (element.hasAttribute('data-processed')) {
            return;
          }

          // Get the diagram definition from text content
          const graphDefinition = element.textContent || element.innerText || '';
          const trimmedDefinition = graphDefinition.trim();
          
          if (!trimmedDefinition) {
            console.warn('Empty Mermaid diagram definition found');
            return;
          }
          
          // Find the title from the parent container
          const parentContainer = element.closest('.bg-white, .bg-gray-50');
          const titleElement = parentContainer?.querySelector('h3, h2');
          const diagramTitle = titleElement?.textContent || `Diagram ${index + 1}`;
          
          // Mark as processing
          element.setAttribute('data-processed', 'processing');
          element.setAttribute('data-diagram-definition', trimmedDefinition);
          element.setAttribute('data-diagram-title', diagramTitle);
          
          // Use individual render for more reliable processing
          const id = `mermaid-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
          
          mermaid.render(id, trimmedDefinition).then(({ svg }) => {
            // console.log('Mermaid diagram rendered successfully:', diagramTitle);
            
            // Verify element is still in DOM
            if (!document.contains(element)) {
              console.warn('Element removed from DOM before rendering completed');
              return;
            }
            
            // Create wrapper for the diagram with controls
            const wrapper = document.createElement('div');
            wrapper.className = 'mermaid-wrapper';
            wrapper.setAttribute('data-mermaid-diagram', trimmedDefinition);
            wrapper.setAttribute('data-mermaid-title', diagramTitle);
            wrapper._mermaidSvg = svg;
            wrapper.style.cssText = 'position: relative; width: 100%; cursor: pointer; margin: 20px 0;';
            
            // Create controls overlay
            const controls = document.createElement('div');
            controls.className = 'mermaid-controls';
            controls.style.cssText = `
              position: absolute;
              top: 10px;
              right: 10px;
              display: flex;
              gap: 8px;
              z-index: 10;
              opacity: 0;
              transition: opacity 0.3s ease;
              background: rgba(255, 255, 255, 0.95);
              padding: 8px;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            `;
            
            // Add expand button
            const expandBtn = document.createElement('button');
            expandBtn.innerHTML = 'Expand';
            expandBtn.className = 'mermaid-expand-btn';
            expandBtn.title = 'View larger (Click diagram to open)';
            expandBtn.style.cssText = `
              background: none;
              border: none;
              cursor: pointer;
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 4px;
              transition: background 0.2s;
              color: #64748B;
              font-weight: 500;
            `;
            expandBtn.onmouseover = () => expandBtn.style.background = '#f0f0f0';
            expandBtn.onmouseout = () => expandBtn.style.background = 'transparent';
            
            // Add download button
            const downloadBtn = document.createElement('button');
            downloadBtn.innerHTML = 'Download';
            downloadBtn.className = 'mermaid-download-btn';
            downloadBtn.title = 'Download as SVG';
            downloadBtn.style.cssText = `
              background: none;
              border: none;
              cursor: pointer;
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 4px;
              transition: background 0.2s;
              color: #64748B;
              font-weight: 500;
            `;
            downloadBtn.onmouseover = () => downloadBtn.style.background = '#f0f0f0';
            downloadBtn.onmouseout = () => downloadBtn.style.background = 'transparent';
            downloadBtn.onclick = (e) => {
              e.stopPropagation();
              const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(svgBlob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${diagramTitle.replace(/\s+/g, '-').toLowerCase()}-${id}.svg`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            };
            
            controls.appendChild(expandBtn);
            controls.appendChild(downloadBtn);
            
            // Create container for SVG
            const svgContainer = document.createElement('div');
            svgContainer.className = 'mermaid-svg-container';
            svgContainer.innerHTML = svg;
            svgContainer.style.cssText = 'transition: opacity 0.2s; display: flex; justify-content: center; width: 100%;';
            
            wrapper.appendChild(svgContainer);
            wrapper.appendChild(controls);
            
            // Show controls on hover
            wrapper.onmouseenter = () => {
              controls.style.opacity = '1';
              svgContainer.style.opacity = '0.95';
            };
            wrapper.onmouseleave = () => {
              controls.style.opacity = '0';
              svgContainer.style.opacity = '1';
            };
            
            // Replace the original element content with wrapper
            element.innerHTML = '';
            element.appendChild(wrapper);
            element.style.cssText = 'position: relative;';
            element.setAttribute('data-processed', 'true');
            
            // console.log('Mermaid diagram displayed:', diagramTitle);
          }).catch((error) => {
            console.error('Mermaid rendering error:', error, 'Diagram:', trimmedDefinition.substring(0, 50));
            // Keep the original content if rendering fails
            element.removeAttribute('data-processed');
          });
        });
      }
    };

    // Use MutationObserver to watch for when .mermaid elements are added to DOM
    const blogContentContainer = document.querySelector('[style*="lineHeight"]') || 
                                 document.querySelector('.prose') ||
                                 document.body;
    
    if (blogContentContainer) {
      observer = new MutationObserver((mutations) => {
        const hasMermaidElements = document.querySelectorAll('.mermaid:not([data-processed])').length > 0;
        if (hasMermaidElements) {
          // Debounce processing
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            requestAnimationFrame(processMermaidDiagrams);
          }, 200);
        }
      });

      observer.observe(blogContentContainer, {
        childList: true,
        subtree: true
      });
    }

    // Initial processing with multiple attempts using requestAnimationFrame
    const attemptProcessing = (attempt = 0) => {
      if (attempt < 8) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          timeoutId = setTimeout(() => {
            const unprocessed = document.querySelectorAll('.mermaid:not([data-processed])').length;
            if (unprocessed > 0) {
              // console.log(`Processing attempt ${attempt + 1}: Found ${unprocessed} unprocessed diagrams`);
              processMermaidDiagrams();
              // Try again if still unprocessed
              attemptProcessing(attempt + 1);
            } else {
              // console.log('All Mermaid diagrams processed successfully');
            }
          }, 100 + (attempt * 200));
        });
      }
    };

    // Start processing attempts - use multiple requestAnimationFrame calls
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        attemptProcessing();
      });
    });

    // Cleanup
    return () => {
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isIndividualBlog, selectedBlog]);

  // Handle clicks on Mermaid diagrams using event delegation
  useEffect(() => {
    if (!isIndividualBlog) return;
    
    const handleMermaidClick = (e) => {
      // Don't open modal if clicking on controls or buttons
      if (e.target.closest('.mermaid-controls') || e.target.closest('button')) {
        return;
      }
      
      const wrapper = e.target.closest('.mermaid-wrapper');
      if (wrapper) {
        const diagram = wrapper.getAttribute('data-mermaid-diagram');
        const title = wrapper.getAttribute('data-mermaid-title');
        const svg = wrapper._mermaidSvg || null;
        
        if (diagram && title) {
          // console.log('Opening Mermaid modal:', title);
          setMermaidModal({
            isOpen: true,
            diagram: diagram,
            title: title,
            svg: svg
          });
          setMermaidZoom(100);
          setIsFullscreen(false);
        }
      }
    };

    const handleMermaidExpand = (e) => {
      if (e.target.classList.contains('mermaid-expand-btn') || e.target.closest('.mermaid-expand-btn')) {
        e.stopPropagation();
        e.preventDefault();
        const wrapper = e.target.closest('.mermaid-wrapper');
        if (wrapper) {
          const diagram = wrapper.getAttribute('data-mermaid-diagram');
          const title = wrapper.getAttribute('data-mermaid-title');
          const svg = wrapper._mermaidSvg || null;
          
          if (diagram && title) {
            // console.log('Opening Mermaid modal from button:', title);
            setMermaidModal({
              isOpen: true,
              diagram: diagram,
              title: title,
              svg: svg
            });
            setMermaidZoom(100);
            setIsFullscreen(false);
          }
        }
      }
    };

    // Use capture phase to ensure we catch the event
    document.addEventListener('click', handleMermaidClick, true);
    document.addEventListener('click', handleMermaidExpand, true);
    
    return () => {
      document.removeEventListener('click', handleMermaidClick, true);
      document.removeEventListener('click', handleMermaidExpand, true);
    };
  }, [isIndividualBlog]);

  // Handle keyboard shortcuts for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (mermaidModal.isOpen) {
        if (e.key === 'Escape') {
          setMermaidModal({ isOpen: false, diagram: null, title: '', svg: null });
          setIsFullscreen(false);
        } else if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          setMermaidZoom(prev => Math.min(1000, prev + 10));
        } else if (e.key === '-') {
          e.preventDefault();
          setMermaidZoom(prev => Math.max(50, prev - 10));
        } else if (e.key === '0') {
          e.preventDefault();
          setMermaidZoom(100);
        } else if (e.key === 'f' || e.key === 'F') {
          e.preventDefault();
          setIsFullscreen(prev => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mermaidModal.isOpen]);

  // Helper functions (must be defined before useMemo and useEffect that use them)
  // Memoize these functions since they're used in useMemo and useEffect
  const getAllCategories = useCallback(() => {
    const categories = [...new Set(allBlogs.map(blog => blog.category))];
    return categories;
  }, [allBlogs]);

  const getAllTags = useCallback(() => {
    const tags = [...new Set(allBlogs.flatMap(blog => blog.tags))];
    return tags;
  }, [allBlogs]);

  // Helper function to normalize tag names for URL matching
  const normalizeTagForUrl = (tag) => {
    return tag.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  };

  // Helper function to extract abbreviation from a tag (first letters of each word)
  const getTagAbbreviation = (tag) => {
    // Split by spaces or hyphens and get first letter of each word
    const words = tag.split(/[\s-]+/).filter(word => word.length > 0);
    return words.map(word => word.charAt(0).toUpperCase()).join('');
  };

  // Helper function to check if a tag matches a URL slug
  // Returns a score: higher score = better match, 0 = no match
  const getTagMatchScore = (tag, urlSlug) => {
    const normalizedTag = normalizeTagForUrl(tag);
    const normalizedSlug = urlSlug.toLowerCase();
    
    // Priority 1: Exact match (highest priority)
    if (normalizedTag === normalizedSlug) {
      return 100;
    }
    
    // Priority 2: First word exact match (e.g., "ai" matches "AI Integration")
    const tagFirstWord = normalizedTag.split('-')[0];
    if (tagFirstWord === normalizedSlug) {
      return 90;
    }
    
    // Priority 3: Tag starts with slug followed by separator (e.g., "ai" matches "ai-integration")
    if (normalizedTag.startsWith(normalizedSlug + '-')) {
      return 80;
    }
    
    // Priority 4: Slug is a complete word in the tag (e.g., "ai" in "machine-learning-ai")
    const tagWords = normalizedTag.split('-');
    if (tagWords.includes(normalizedSlug)) {
      return 70;
    }
    
    // Priority 5: Abbreviation exact match (e.g., "ml" matches "Machine Learning")
    const tagAbbreviation = getTagAbbreviation(tag).toLowerCase();
    if (tagAbbreviation === normalizedSlug) {
      return 60;
    }
    
    // Priority 6: Abbreviation starts with slug (only for 2+ character slugs to avoid false positives)
    if (normalizedSlug.length >= 2 && tagAbbreviation.startsWith(normalizedSlug)) {
      return 50;
    }
    
    return 0; // No match
  };

  // Helper function to check if a tag matches a URL slug
  const tagMatchesSlug = (tag, urlSlug) => {
    return getTagMatchScore(tag, urlSlug) > 0;
  };

  // Handle individual blog page or tag filtering
  useEffect(() => {
    // Wait for blogs to finish loading before processing slug
    // This prevents premature redirects when navigating directly to a blog URL or refreshing
    if (isLoading) {
      return;
    }

    // If we have a slug, process it only after blogs are loaded
    if (slug) {
      // If no blogs are available, don't process (edge case)
      if (allBlogs.length === 0) {
        return;
      }
      // First, check if it's a valid blog slug
      const blog = allBlogs.find(b => b.slug === slug);
      if (blog) {
        setSelectedBlog(blog);
        setIsIndividualBlog(true);
        // Retrieve the previous tag filter from sessionStorage (set when user was on tag page)
        const storedTag = sessionStorage.getItem('blogTagFilter');
        if (storedTag) {
          setPreviousTagFilter(storedTag);
        } else {
          setPreviousTagFilter(null);
        }
        setIsTagFilter(false);
        setSelectedTag('All');
      } else {
        // If not a blog slug, check if it's a tag
        // Find ALL matching tags (not just the best one)
        const allTags = getAllTags();
        const matchingTags = [];
        let bestMatch = null;
        let bestScore = 0;
        
        allTags.forEach(tag => {
          const score = getTagMatchScore(tag, slug);
          if (score > 0) {
            matchingTags.push(tag);
            // Also track the best match for display purposes
            if (score > bestScore) {
              bestScore = score;
              bestMatch = tag;
            }
          }
        });
        
        if (matchingTags.length > 0) {
          // It's a tag filter - store the best match in sessionStorage for back button
          sessionStorage.setItem('blogTagFilter', bestMatch || matchingTags[0]);
          setIsTagFilter(true);
          setFilteredTag(bestMatch || matchingTags[0]); // Keep best match for backward compatibility
          setFilteredTags(matchingTags); // Store all matching tags
          setSelectedTag(bestMatch || matchingTags[0]);
          setIsIndividualBlog(false);
          setSelectedBlog(null);
          setPreviousTagFilter(null);
        } else {
          // Neither a blog slug nor a valid tag, redirect to blog listing
          sessionStorage.removeItem('blogTagFilter');
          setIsTagFilter(false);
          setFilteredTag(null);
          setFilteredTags([]);
          navigate('/blog');
        }
      }
    } else if (!slug) {
      // On main blog listing page - clear stored tag filter
      sessionStorage.removeItem('blogTagFilter');
      setIsIndividualBlog(false);
      setIsTagFilter(false);
      setSelectedBlog(null);
      setFilteredTag(null);
      setFilteredTags([]);
      setPreviousTagFilter(null);
      setSelectedTag('All');
    }
  }, [slug, navigate, allBlogs, getAllTags, isLoading]);

  // Compute categories and tags (must be before early return)
  const categories = useMemo(() => ['All', ...getAllCategories()], [getAllCategories]);
  const tags = useMemo(() => ['All', ...getAllTags()], [getAllTags]);
  
  // Memoize filtered blogs to avoid re-filtering on every render (must be before early return)
  const filteredBlogs = useMemo(() => {
    return allBlogs.filter(blog => {
      const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
      
      // If we're filtering by tag from URL, check if blog has ANY of the matching tags
      let matchesTag = true;
      if (isTagFilter && filteredTags.length > 0) {
        // Check if blog has any of the matching tags
        matchesTag = blog.tags.some(tag => filteredTags.includes(tag));
      } else if (isTagFilter && filteredTag) {
        // Fallback to single tag for backward compatibility
        matchesTag = blog.tags.includes(filteredTag);
      } else if (selectedTag !== 'All') {
        // Using dropdown filter
        matchesTag = blog.tags.includes(selectedTag);
      }
      
      const matchesSearch = searchTerm === '' || 
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesCategory && matchesTag && matchesSearch;
    });
  }, [allBlogs, selectedCategory, isTagFilter, filteredTags, filteredTag, selectedTag, searchTerm]);

  // Get recommended articles based on tags and category
  const recommendedArticles = useMemo(() => {
    if (!selectedBlog || allBlogs.length === 0) {
      return [];
    }

    // Score articles based on relevance
    const scoredArticles = allBlogs
      .filter(blog => blog.id !== selectedBlog.id) // Exclude current article
      .map(blog => {
        let score = 0;
        
        // Calculate tag overlap score (higher weight)
        const currentTags = selectedBlog.tags || [];
        const blogTags = blog.tags || [];
        const commonTags = currentTags.filter(tag => blogTags.includes(tag));
        score += commonTags.length * 10; // 10 points per matching tag
        
        // Category match (medium weight)
        if (blog.category === selectedBlog.category) {
          score += 5;
        }
        
        return { blog, score, commonTags };
      })
      .filter(item => item.score > 0) // Only include articles with some relevance
      .sort((a, b) => b.score - a.score) // Sort by score descending
      .slice(0, 6); // Get top 6 articles

    return scoredArticles.map(item => item.blog);
  }, [selectedBlog, allBlogs]);

  // Show initial loading state (only when no blogs are loaded yet)
  // Also show loading if we have a slug but are still loading (prevents showing listing page prematurely)
  if (isLoading && (allBlogs.length === 0 || slug)) {
    return (
      <>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ minHeight: '100vh', backgroundColor: '#f4faf8' }}>
          <Header />
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: 'calc(100vh - 105px)' // Adjust based on your header height
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', color: '#64748B', marginBottom: '20px' }}>
                Loading blogs{totalBlogs > 0 ? ` (${loadedCount}/${totalBlogs})` : '...'}
              </div>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f1f5f9', 
                borderTop: '4px solid #00d690', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Enhanced SEO Data
  const seoData = {
    title: 'Blog - OctalChip | Web Development, Mobile Apps, AI & DevOps Insights',
    description: 'Read our latest insights on web development, mobile app development, AI integration, DevOps, and cloud infrastructure. Expert guides and best practices from OctalChip development team.',
    keywords: 'web development blog, mobile app development blog, AI development blog, DevOps blog, cloud infrastructure blog, software development insights, technology trends, development best practices, programming tutorials, tech blog',
    canonical: 'https://www.octalchip.com/blog',
    tags: ['web development blog', 'mobile app development', 'AI development', 'DevOps', 'cloud infrastructure'],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Blog', url: 'https://www.octalchip.com/blog' }
    ],
    faqData: blogFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "OctalChip Development Blog",
      "description": "Expert insights on web development, mobile apps, AI integration, DevOps, and cloud infrastructure",
      "url": "https://www.octalchip.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "OctalChip",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.octalchip.com/favicon.svg"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com",
          "telephone": "+91-7574082582"
        }
      },
      "blogPost": allBlogs.map(blog => ({
        "@type": "BlogPosting",
        "headline": blog.title,
        "description": blog.shortDescription,
        "url": `https://www.octalchip.com/blog/${blog.slug}`,
        "datePublished": blog.publishDate,
        "dateModified": blog.publishDate,
        "author": {
          "@type": "Organization",
          "name": "OctalChip"
        },
        "publisher": {
          "@type": "Organization",
          "name": "OctalChip",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.octalchip.com/favicon.svg"
          }
        },
        "articleSection": blog.category,
        "keywords": blog.keywords,
        "wordCount": blog.readTime
      }))
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleContactInputChange = (e) => {
    setContactFormData({
      ...contactFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    setContactSubmitMessage('');

    try {
      await sendContactFormEmail(contactFormData);
      setContactSubmitMessage('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
      setContactFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setContactSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const contactInfo = [
    {
      icon: 'MapPin',
      title: 'Head Office',
      details: [
        'Octalchip',
        '404, Opera point, kothariya,',
        'gujarat, india, 360022'
      ],
      link: 'https://maps.google.com/?q=404+Opera+point+kothariya+gujarat+india+360022'
    },
    {
      icon: 'Mail',
      title: 'Email',
      details: ['business@octalchip.com'],
      link: 'mailto:business@octalchip.com'
    },
    {
      icon: 'Phone',
      title: 'Phone',
      details: ['+91 7574082582'],
      link: 'tel:+917574082582'
    },
    {
      icon: 'Clock',
      title: 'Working Hours',
      details: ['Mon - Fri, 9AM - 6PM'],
      link: null
    }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: 'https://www.facebook.com/octalchip' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://www.instagram.com/octalchiptech/' },
    { name: 'LinkedIn', icon: 'Linkedin', url: 'https://www.linkedin.com/company/octalchip' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://x.com/octalchip' },
    { name: 'Discord', icon: 'Discord', url: 'https://discord.gg/octalchip' }
  ];


  // If we're on an individual blog page, render the blog detail view
  if (isIndividualBlog && selectedBlog) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f4faf8' }}>
        <style>{`
          @media (max-width: 640px) {
            .blog-title-h1 { font-size: 28px !important; }
            .blog-desc-p { font-size: 16px !important; }
            .blog-section-padding { padding: 40px 0 !important; }
            .blog-content-padding { padding: 20px !important; }
            .blog-container-padding { padding: 20px 16px !important; }
            .blog-h2 { font-size: 24px !important; }
            .blog-h3 { font-size: 18px !important; }
            .blog-text-sm { font-size: 14px !important; }
            .blog-card-title { font-size: 18px !important; }
            .blog-card-desc { font-size: 13px !important; }
            .blog-hero-padding { padding: 40px 0 !important; }
            .blog-filter-padding { padding: 24px 0 !important; }
            .blog-cta-padding { padding: 40px 0 !important; }
            .blog-grid-min { grid-template-columns: 1fr !important; }
            .blog-grid-gap { gap: 24px !important; }
            .blog-modal-header { padding: 16px !important; font-size: 18px !important; }
            .blog-modal-content { padding: 16px !important; }
            .blog-button-padding { padding: 12px 24px !important; font-size: 16px !important; }
            .blog-input-padding { padding: 12px 16px 12px 44px !important; font-size: 14px !important; }
            .blog-select-padding { padding: 10px 12px !important; font-size: 14px !important; min-width: 120px !important; }
            .blog-card-padding { padding: 20px !important; }
            .blog-badge-font { font-size: 11px !important; padding: 3px 10px !important; }
            .blog-icon-text { font-size: 14px !important; }
            .blog-link-padding { padding: 10px 14px !important; font-size: 13px !important; }
            .blog-modal-zoom-controls { flex-wrap: wrap !important; gap: 4px !important; }
            .blog-modal-zoom-text { font-size: 12px !important; min-width: 40px !important; }
          }
          @media (min-width: 641px) and (max-width: 768px) {
            .blog-title-h1 { font-size: 36px !important; }
            .blog-desc-p { font-size: 18px !important; }
            .blog-section-padding { padding: 60px 0 !important; }
            .blog-content-padding { padding: 32px !important; }
            .blog-container-padding { padding: 32px 20px !important; }
            .blog-grid-min { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)) !important; }
          }
        `}</style>
        <Header />
        <SEO 
          title={selectedBlog.title}
          description={selectedBlog.metaDescription}
          keywords={selectedBlog.keywords}
          canonical={`https://www.octalchip.com/blog/${selectedBlog.slug}`}
          ogType="article"
          author="OctalChip"
          publishDate={selectedBlog.publishDate}
          modifiedDate={selectedBlog.publishDate}
          articleSection={selectedBlog.category}
          tags={selectedBlog.tags}
          breadcrumbs={[
            { name: 'Home', url: 'https://www.octalchip.com/' },
            { name: 'Blog', url: 'https://www.octalchip.com/blog' },
            { name: selectedBlog.title, url: `https://www.octalchip.com/blog/${selectedBlog.slug}` }
          ]}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": selectedBlog.title,
            "description": selectedBlog.metaDescription,
            "datePublished": selectedBlog.publishDate,
            "dateModified": selectedBlog.publishDate,
            "author": {
              "@type": "Organization",
              "name": "OctalChip",
              "url": "https://www.octalchip.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "OctalChip",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.octalchip.com/favicon.svg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.octalchip.com/blog/${selectedBlog.slug}`
            },
            "articleSection": selectedBlog.category,
            "keywords": selectedBlog.keywords,
            "wordCount": selectedBlog.readTime,
            "inLanguage": "en-US"
          }}
        />
        
        {/* Individual Blog Page Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-10 md:py-12">
          {/* Back Button */}
          <div className="mb-6 sm:mb-8">
            <a
              href={previousTagFilter ? `/blog/${normalizeTagForUrl(previousTagFilter)}` : '/blog'}
              onClick={(e) => {
                e.preventDefault();
                if (previousTagFilter) {
                  navigate(`/blog/${normalizeTagForUrl(previousTagFilter)}`);
                } else {
                  navigate('/blog');
                }
              }}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 no-underline text-sm sm:text-base font-medium px-3 sm:px-4 py-2 rounded-lg bg-white hover:bg-gray-50 shadow-sm hover:shadow-md transition-all group"
            >
              <Icon name="ArrowLeft" size={14} className="sm:w-4 sm:h-4 transition-transform group-hover:-translate-x-1" />
              Back to Blog
            </a>
          </div>

          {/* Blog Header */}
          <div className="mb-8 sm:mb-10 md:mb-12">
            <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-5 flex-wrap">
              <span className="bg-[#00d690] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                {selectedBlog.category}
              </span>
              <span className="bg-gray-100 text-gray-500 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                {selectedBlog.readTime}
              </span>
              <span className="bg-gray-100 text-gray-500 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                {formatDate(selectedBlog.publishDate)}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-5 leading-tight">
              {selectedBlog.title}
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-500 leading-relaxed mb-6 sm:mb-8">
              {selectedBlog.shortDescription}
            </p>

            <div className="flex items-center gap-3 sm:gap-4 flex-wrap mt-4 sm:mt-5">
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm md:text-base text-gray-500">
                  {formatDate(selectedBlog.publishDate)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm md:text-base text-gray-500">
                  {selectedBlog.readTime}
                </span>
              </div>
            </div>
          </div>

          {/* Blog Content */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 mb-8 sm:mb-10 md:mb-12 shadow-sm">
            <div className="leading-relaxed text-sm sm:text-base md:text-lg text-gray-700 prose prose-sm sm:prose-base md:prose-lg max-w-none">
              {selectedBlog.content}
            </div>
            {/* Add styles for Mermaid diagrams */}
            <style>{`
              .mermaid {
                display: flex;
                justify-content: center;
                margin: 20px 0;
                overflow-x: auto;
              }
              .mermaid svg {
                max-width: 100%;
                height: auto;
              }
              .mermaid-wrapper {
                position: relative;
                width: 100%;
              }
              .mermaid-wrapper:hover {
                opacity: 0.95;
              }
            `}</style>
          </div>

          {/* Recommended Articles Section */}
          {recommendedArticles.length > 0 && (
            <div className="mb-8 sm:mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-6 sm:mb-8">
                Recommended Articles
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                {recommendedArticles.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    {/* Blog Header */}
                    <div className="p-4 sm:p-5 md:p-6">
                      <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4 flex-wrap">
                        <span className="bg-[#00d690] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                          {blog.category}
                        </span>
                        <span className="bg-gray-100 text-gray-500 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                          {blog.readTime}
                        </span>
                      </div>

                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 leading-snug line-clamp-2">
                        {blog.title}
                      </h3>

                      <p className="text-xs sm:text-sm text-gray-500 leading-relaxed line-clamp-3 mb-3 sm:mb-4">
                        {blog.shortDescription}
                      </p>
                    </div>

                    {/* Blog Footer */}
                    <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 mt-auto border-t border-gray-100">
                      <div className="flex items-center gap-3 sm:gap-4 flex-wrap mb-2 sm:mb-3">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Icon name="Calendar" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                          <span className="text-xs sm:text-sm text-gray-500">
                            {formatDate(blog.publishDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Icon name="Clock" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                          <span className="text-xs sm:text-sm text-gray-500">
                            {blog.readTime}
                          </span>
                        </div>
                      </div>
                      
                      {/* Tags */}
                      {blog.tags && blog.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {blog.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="bg-gray-50 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium"
                            >
                              {tag}
                            </span>
                          ))}
                          {blog.tags.length > 3 && (
                            <span className="bg-gray-50 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                              +{blog.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Mermaid Diagram Modal */}
          {mermaidModal.isOpen && (
            <div
              className="fixed inset-0 bg-black/85 z-[9999] flex items-center justify-center p-4 sm:p-5 md:p-6"
              style={{ animation: 'fadeIn 0.3s ease' }}
              onClick={() => {
                if (!isFullscreen) {
                  setMermaidModal({ isOpen: false, diagram: null, title: '', svg: null });
                  setIsFullscreen(false);
                }
              }}
            >
              <style>{`
                @keyframes fadeIn {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                @keyframes slideUp {
                  from { transform: translateY(20px); opacity: 0; }
                  to { transform: translateY(0); opacity: 1; }
                }
              `}</style>
              <div
                className={`bg-white ${isFullscreen ? 'rounded-none' : 'rounded-xl sm:rounded-2xl'} ${isFullscreen ? 'w-screen h-screen' : 'w-full max-w-[95vw] max-h-[95vh]'} flex flex-col shadow-2xl overflow-hidden relative`}
                style={{ animation: 'slideUp 0.3s ease' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="p-3 sm:p-4 md:p-5 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 bg-gray-50 flex-shrink-0 sticky top-0 z-[1000] shadow-sm">
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 m-0 flex-1 min-w-0">
                    {mermaidModal.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
                    {/* Zoom Controls */}
                    <div className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-gray-200 flex-wrap">
                      <button
                        onClick={() => setMermaidZoom(prev => Math.max(50, prev - 10))}
                        className="bg-transparent border-none cursor-pointer text-base sm:text-lg md:text-xl px-2 sm:px-3 text-gray-500 hover:text-gray-800 transition-colors font-bold min-w-[28px] sm:min-w-[32px]"
                        title="Zoom Out (-)"
                      >
                        -
                      </button>
                      <span className="text-xs sm:text-sm font-semibold text-gray-800 min-w-[40px] sm:min-w-[50px] text-center">
                        {mermaidZoom}%
                      </span>
                      <button
                        onClick={() => setMermaidZoom(prev => Math.min(1000, prev + 10))}
                        className="bg-transparent border-none cursor-pointer text-base sm:text-lg md:text-xl px-2 sm:px-3 text-gray-500 hover:text-gray-800 transition-colors font-bold min-w-[28px] sm:min-w-[32px]"
                        title="Zoom In (+)"
                      >
                        +
                      </button>
                      <button
                        onClick={() => setMermaidZoom(100)}
                        className="bg-transparent border-none cursor-pointer text-[10px] sm:text-xs px-1.5 sm:px-2 ml-1 text-gray-500 hover:text-gray-800 transition-colors font-medium"
                        title="Reset Zoom (0)"
                      >
                        Reset
                      </button>
                    </div>
                    
                    {/* Fullscreen Toggle */}
                    <button
                      onClick={() => setIsFullscreen(prev => !prev)}
                      className="bg-transparent hover:bg-gray-100 border border-gray-200 cursor-pointer text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-gray-500 hover:text-gray-800 transition-all font-medium"
                      title="Toggle Fullscreen (F)"
                    >
                      <span className="hidden sm:inline">{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
                      <span className="sm:hidden">{isFullscreen ? 'Exit' : 'Full'}</span>
                    </button>
                    
                    {/* Download Button */}
                    <button
                      onClick={async () => {
                        try {
                          if (mermaidModal.svg) {
                            const svgBlob = new Blob([mermaidModal.svg], { type: 'image/svg+xml' });
                            const url = URL.createObjectURL(svgBlob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${mermaidModal.title.replace(/\s+/g, '-').toLowerCase()}.svg`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } else if (mermaidModal.diagram) {
                            const id = `mermaid-download-${Date.now()}`;
                            const { svg } = await mermaid.render(id, mermaidModal.diagram);
                            const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
                            const url = URL.createObjectURL(svgBlob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${mermaidModal.title.replace(/\s+/g, '-').toLowerCase()}.svg`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }
                        } catch (error) {
                          console.error('Download error:', error);
                        }
                      }}
                      className="bg-[#00d690] hover:bg-[#00b876] text-white border-none cursor-pointer text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold transition-colors"
                      title="Download as SVG"
                    >
                      Download
                    </button>
                    
                    {/* Close Button */}
                    <button
                      onClick={() => {
                        setMermaidModal({ isOpen: false, diagram: null, title: '', svg: null });
                        setIsFullscreen(false);
                      }}
                      className="bg-transparent hover:bg-gray-100 border-none cursor-pointer text-lg sm:text-xl md:text-2xl px-2 sm:px-3 py-1 text-gray-500 hover:text-gray-800 rounded-lg transition-colors font-bold leading-none"
                      onMouseOver={(e) => {
                        e.target.style.background = '#f1f5f9';
                        e.target.style.color = '#1E293B';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'none';
                        e.target.style.color = '#64748B';
                      }}
                      title="Close (Esc)"
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                {/* Modal Content */}
                <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-5 flex justify-center items-start bg-gray-50 relative min-h-0 w-full">
                  {mermaidModal.svg ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        width: '100%',
                        minHeight: '100%',
                        padding: '0',
                        boxSizing: 'border-box',
                        position: 'relative',
                        marginTop: 0
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-block',
                          transform: `scale(${mermaidZoom / 100})`,
                          transformOrigin: 'top center',
                          transition: 'transform 0.3s ease',
                          maxWidth: '100%',
                          position: 'relative',
                          marginTop: 0,
                          marginBottom: 0,
                          willChange: 'transform'
                        }}
                        dangerouslySetInnerHTML={{ __html: mermaidModal.svg }}
                      />
                    </div>
                  ) : mermaidModal.diagram ? (
                    <MermaidModalRenderer 
                      diagram={mermaidModal.diagram} 
                      zoom={mermaidZoom}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#64748B' }}>
                      No diagram available
                    </div>
                  )}
                </div>
                
                {/* Keyboard Shortcuts Hint */}
                {!isFullscreen && (
                  <div className="p-2 sm:p-3 md:p-4 border-t border-gray-200 bg-gray-50 text-[10px] sm:text-xs text-gray-500 text-center flex-shrink-0 relative z-10">
                    <strong className="hidden sm:inline">Keyboard Shortcuts:</strong>
                    <span className="hidden sm:inline"> </span>
                    <kbd className="px-1 sm:px-1.5 py-0.5 bg-white rounded text-[10px] sm:text-xs mx-0.5 sm:mx-1">+</kbd>
                    <span className="hidden md:inline"> Zoom In</span>
                    <span className="sm:hidden">+</span>
                    <span className="hidden sm:inline"> | </span>
                    <kbd className="px-1 sm:px-1.5 py-0.5 bg-white rounded text-[10px] sm:text-xs mx-0.5 sm:mx-1">-</kbd>
                    <span className="hidden md:inline"> Zoom Out</span>
                    <span className="sm:hidden">-</span>
                    <span className="hidden sm:inline"> | </span>
                    <kbd className="px-1 sm:px-1.5 py-0.5 bg-white rounded text-[10px] sm:text-xs mx-0.5 sm:mx-1">0</kbd>
                    <span className="hidden md:inline"> Reset</span>
                    <span className="sm:hidden">0</span>
                    <span className="hidden sm:inline"> | </span>
                    <kbd className="px-1 sm:px-1.5 py-0.5 bg-white rounded text-[10px] sm:text-xs mx-0.5 sm:mx-1">F</kbd>
                    <span className="hidden md:inline"> Fullscreen</span>
                    <span className="sm:hidden">F</span>
                    <span className="hidden sm:inline"> | </span>
                    <kbd className="px-1 sm:px-1.5 py-0.5 bg-white rounded text-[10px] sm:text-xs mx-0.5 sm:mx-1">Esc</kbd>
                    <span className="hidden md:inline"> Close</span>
                    <span className="sm:hidden">Esc</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Internal and External Links */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 mb-8 sm:mb-10 md:mb-12 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
              {/* Internal Links */}
              <div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-5">
                  Related Services
                </h3>
                <div className="flex flex-col gap-2 sm:gap-3">
                  {selectedBlog.internalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      className="block px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 hover:bg-gray-200 rounded-lg text-gray-700 no-underline text-xs sm:text-sm font-medium transition-all border border-gray-200 hover:-translate-y-0.5"
                    >
                      {link.text}
                    </a>
                  ))}
                </div>
              </div>

              {/* External Links */}
              <div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-5">
                  External Resources
                </h3>
                <div className="flex flex-col gap-2 sm:gap-3">
                  {selectedBlog.externalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-700 no-underline text-xs sm:text-sm font-medium transition-all border border-blue-200 hover:-translate-y-0.5"
                    >
                      {link.text} ↗
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section - Blog Style */}
        <section id="contact" className="bg-gradient-to-br from-green-50 to-gray-50 py-12 sm:py-16 md:py-20 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10">
            {/* Compact Header */}
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <div className="inline-flex items-center gap-2 mb-3 text-[#00d690] text-xs sm:text-sm font-semibold">
                <Icon name="MessageSquare" size={16} className="sm:w-5 sm:h-5" />
                <span>Let's Connect</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                Questions or Project Ideas?
              </h2>
              <p className="text-sm sm:text-base text-gray-500 max-w-lg mx-auto">
                Drop us a message below or reach out directly. We typically respond within 24 hours.
              </p>
            </div>

            {/* Main Content - Side by Side Layout */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 shadow-lg border border-gray-200">
              <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 sm:gap-8 md:gap-10">
                {/* Contact Form - Left Side */}
                <div>
                  <form onSubmit={handleContactSubmit} className="flex flex-col gap-3 sm:gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <input
                          type="text"
                          name="name"
                          value={contactFormData.name}
                          onChange={handleContactInputChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="Your name *"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          value={contactFormData.email}
                          onChange={handleContactInputChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="Your email *"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <input
                          type="tel"
                          name="phone"
                          value={contactFormData.phone}
                          onChange={handleContactInputChange}
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="Phone number"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          name="subject"
                          value={contactFormData.subject}
                          onChange={handleContactInputChange}
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="Subject *"
                        />
                      </div>
                    </div>
                    
                    <textarea
                      name="message"
                      value={contactFormData.message}
                      onChange={handleContactInputChange}
                      required
                      rows={5}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20 transition-all bg-gray-50 focus:bg-white font-inherit resize-y"
                      placeholder="Your message *"
                    ></textarea>
                    
                    {contactSubmitMessage && (
                      <div className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm ${
                        contactSubmitMessage.includes('Thank you') 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {contactSubmitMessage}
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      disabled={isSubmittingContact}
                      className="w-full bg-[#00d690] hover:bg-[#00b876] disabled:bg-gray-400 disabled:opacity-60 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold border-none cursor-pointer disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 disabled:hover:transform-none hover:-translate-y-0.5"
                    >
                      {isSubmittingContact ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Icon name="Send" size={16} className="sm:w-5 sm:h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </div>
                
                {/* Contact Info - Right Side */}
                <div className="flex flex-col gap-4 sm:gap-6 lg:pl-6 lg:border-l lg:border-gray-200">
                  {/* Quick Contact Methods */}
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                      <Icon name="Zap" size={16} className="text-[#00d690] sm:w-5 sm:h-5" />
                      Quick Contact
                    </h4>
                    <div className="flex flex-col gap-2 sm:gap-2.5">
                      <a
                        href="mailto:business@octalchip.com"
                        className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-gray-500 hover:text-[#00d690] hover:bg-green-50 no-underline text-xs sm:text-sm transition-all bg-gray-50"
                      >
                        <Icon name="Mail" size={14} className="sm:w-4 sm:h-4" />
                        <span className="break-all">business@octalchip.com</span>
                      </a>
                      <a
                        href="tel:+917574082582"
                        className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-gray-500 hover:text-[#00d690] hover:bg-green-50 no-underline text-xs sm:text-sm transition-all bg-gray-50"
                      >
                        <Icon name="Phone" size={14} className="sm:w-4 sm:h-4" />
                        <span>+91 7574082582</span>
                      </a>
                      <a
                        href="https://wa.me/917574082582"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-gray-500 hover:text-[#00d690] hover:bg-green-50 no-underline text-xs sm:text-sm transition-all bg-gray-50"
                      >
                        <Icon name="MessageCircle" size={14} className="sm:w-4 sm:h-4" />
                        <span>WhatsApp</span>
                      </a>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2">
                      <Icon name="Share2" size={16} className="text-[#00d690] sm:w-5 sm:h-5" />
                      Follow Us
                    </h4>
                    <div className="flex gap-2 flex-wrap">
                      {socialLinks.map((social, index) => (
                        <a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 sm:w-9 sm:h-9 bg-gray-100 hover:bg-[#00d690] rounded-lg flex items-center justify-center no-underline transition-all hover:scale-110"
                          title={social.name}
                        >
                          <Icon name={social.icon} size={14} className="text-gray-500 hover:text-white transition-colors sm:w-4 sm:h-4" />
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Icon name="MapPin" size={16} className="text-[#00d690] sm:w-5 sm:h-5" />
                      Location
                    </h4>
                    <a
                      href="https://maps.google.com/?q=404+Opera+point+kothariya+gujarat+india+360022"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-[#00d690] text-xs sm:text-sm leading-relaxed no-underline transition-colors"
                    >
                      404, Opera point, kothariya,<br />
                      gujarat, india, 360022
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Default blog listing page
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f4faf8' }}>
      <Header />
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />

      {/* Hero Section */}
      <section id="hero" className="bg-white py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 leading-tight">
              {isTagFilter && filteredTags.length > 0 
                ? `Blogs: ${filteredTags.length === 1 ? filteredTags[0] : filteredTags.join(', ')}`
                : 'Our Development Blog'}
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-500 mb-6 sm:mb-8 leading-relaxed">
              {isTagFilter && filteredTags.length > 0
                ? filteredTags.length === 1
                  ? `Discover our latest articles and insights about ${filteredTags[0]}. Explore expert content from our development team.`
                  : `Discover our latest articles and insights about ${filteredTags.join(', ')}. Explore expert content from our development team.`
                : 'Stay updated with the latest insights, trends, and best practices in web development, mobile apps, AI integration, and DevOps from our expert development team.'}
            </p>
            {isTagFilter && filteredTags.length > 0 && (
              <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:gap-4 items-center">
                {/* Display all matching tags */}
                {filteredTags.length > 1 && (
                  <div className="flex flex-wrap gap-2 justify-center mb-2">
                    {filteredTags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-[#00d690] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <a
                  href="/blog"
                  className="inline-flex items-center gap-2 text-[#00d690] hover:text-white no-underline text-sm sm:text-base font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-green-50 hover:bg-[#00d690] border border-[#00d690] transition-all"
                >
                  <Icon name="ArrowLeft" size={14} className="sm:w-4 sm:h-4" />
                  View All Blogs
                </a>
              </div>
            )}
            <div className="flex justify-center gap-3 sm:gap-4 md:gap-6 flex-wrap">
              <div className="flex items-center gap-2 text-[#00d690]">
                <Icon name="BookOpen" size={16} className="sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm md:text-base font-semibold">{allBlogs.length} Articles</span>
              </div>
              <div className="flex items-center gap-2 text-[#00d690]">
                <Icon name="Users" size={16} className="sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm md:text-base font-semibold">Expert Authors</span>
              </div>
              <div className="flex items-center gap-2 text-[#00d690]">
                <Icon name="TrendingUp" size={16} className="sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm md:text-base font-semibold">Latest Trends</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section id="filters" className="bg-white py-6 sm:py-8 md:py-10 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="flex flex-col gap-4 sm:gap-6">
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 sm:px-4 md:px-5 pl-10 sm:pl-12 md:pl-14 py-2.5 sm:py-3 md:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl text-sm sm:text-base focus:outline-none focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20 transition-all bg-white shadow-sm"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00d690';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 214, 144, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                  }}
                />
                <Icon 
                  name="Search" 
                  size={16} 
                  className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none sm:w-5 sm:h-5" 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-1 rounded text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <Icon name="X" size={14} className="sm:w-4 sm:h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Category and Tag Filters */}
            <div className="flex gap-4 sm:gap-6 justify-center flex-wrap">
              {/* Category Filter */}
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <label className="text-xs sm:text-sm font-semibold text-gray-700">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 border-2 border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-[#00d690] cursor-pointer min-w-[120px] sm:min-w-[150px]"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <label className="text-xs sm:text-sm font-semibold text-gray-700">Tag</label>
                <select
                  value={isTagFilter && filteredTag ? filteredTag : selectedTag}
                  onChange={(e) => {
                    const newTag = e.target.value;
                    setSelectedTag(newTag);
                    setIsTagFilter(newTag !== 'All');
                    setFilteredTag(newTag !== 'All' ? newTag : null);
                    if (newTag === 'All') {
                      navigate('/blog');
                    } else {
                      navigate(`/blog/${normalizeTagForUrl(newTag)}`);
                    }
                  }}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 border-2 border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-[#00d690] cursor-pointer min-w-[120px] sm:min-w-[150px]"
                >
                  {tags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Cards Section */}
      <section id="blog-cards" className="bg-gray-50 py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-12 sm:py-16 md:py-20">
              <div className="flex flex-col items-center gap-4 sm:gap-6">
                <Icon name="Search" size={32} className="sm:w-12 sm:h-12 text-gray-300" />
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-600 mb-2 sm:mb-3">
                    No articles found
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
                    Try adjusting your search criteria or browse all articles
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedTag('All');
                  setIsTagFilter(false);
                  setFilteredTag(null);
                  setFilteredTags([]);
                  navigate('/blog');
                }}
                className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg text-sm sm:text-base font-semibold border-none cursor-pointer transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3">
                  {filteredBlogs.length} Article{filteredBlogs.length !== 1 ? 's' : ''} Found
                  {isTagFilter && filteredTags.length > 0 && ` for "${filteredTags.length === 1 ? filteredTags[0] : filteredTags.join(', ')}"`}
                </h2>
                <p className="text-xs sm:text-sm md:text-base text-gray-500 mb-3 sm:mb-4">
                  {selectedCategory !== 'All' && `Category: ${selectedCategory}`}
                  {selectedCategory !== 'All' && (selectedTag !== 'All' || (isTagFilter && filteredTags.length > 0)) && ' • '}
                  {(selectedTag !== 'All' || (isTagFilter && filteredTags.length > 0)) && (
                    <span>
                      Tag{filteredTags.length > 1 ? 's' : ''}: {
                        isTagFilter && filteredTags.length > 0 
                          ? filteredTags.join(', ')
                          : selectedTag
                      }
                    </span>
                  )}
                  {searchTerm && ` • Search: "${searchTerm}"`}
                </p>
                {isTagFilter && filteredTags.length > 1 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {filteredTags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-green-50 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border border-[#00d690]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {filteredBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >

                      {/* Blog Header */}
                      <div className="p-4 sm:p-5 md:p-6">
                        <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4 flex-wrap">
                          <span className="bg-[#00d690] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                            {blog.category}
                          </span>
                          <span className="bg-gray-100 text-gray-500 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                            {blog.readTime}
                          </span>
                        </div>

                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 leading-snug line-clamp-2">
                          {blog.title}
                        </h3>

                        <p className="text-xs sm:text-sm text-gray-500 leading-relaxed line-clamp-3 mb-3 sm:mb-4">
                          {blog.shortDescription}
                        </p>
                      </div>

                      {/* Blog Footer */}
                      <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 mt-auto border-t border-gray-100">
                        <div className="flex items-center gap-3 sm:gap-4 flex-wrap mb-2 sm:mb-3">
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <Icon name="Calendar" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                            <span className="text-xs sm:text-sm text-gray-500">
                              {formatDate(blog.publishDate)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <Icon name="Clock" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                            <span className="text-xs sm:text-sm text-gray-500">
                              {blog.readTime}
                            </span>
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                          {blog.tags.slice(0, 3).map((tag, index) => (
                            <Link
                              key={index}
                              to={`/blog/${normalizeTagForUrl(tag)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigate(`/blog/${normalizeTagForUrl(tag)}`);
                              }}
                              className="bg-gray-50 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-xl text-[10px] sm:text-xs font-medium no-underline cursor-pointer transition-all inline-block hover:bg-[#00d690] hover:text-white hover:scale-105"
                            >
                              {tag}
                            </Link>
                          ))}
                          {blog.tags.length > 3 && (
                            <span className="bg-gray-50 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-xl text-[10px] sm:text-xs font-medium inline-block">
                              +{blog.tags.length - 3} more
                            </span>
                          )}
                        </div>

                        {/* Read More Button */}
                        <div className="mt-3 sm:mt-4 text-center">
                          <span 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/blog/${blog.slug}`);
                            }}
                            className="inline-flex items-center text-[#00d690] hover:text-[#00b876] font-semibold text-xs sm:text-sm cursor-pointer transition-colors no-underline group"
                          >
                            Read More
                            <Icon name="ArrowRight" size={14} className="sm:w-4 sm:h-4 ml-1 sm:ml-2 transition-transform group-hover:translate-x-1" />
                          </span>
                        </div>
                      </div>
                    </Link>
                ))}
              </div>
              
              {/* Progressive Loading Indicator */}
              {isLoading && loadedCount < totalBlogs && (
                <div className="mt-8 sm:mt-10 md:mt-12 text-center">
                  <div className="inline-flex flex-col items-center gap-3 sm:gap-4">
                    <div className="relative">
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        border: '4px solid #f1f5f9', 
                        borderTop: '4px solid #00d690', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite',
                      }}></div>
                    </div>
                    <p className="text-sm sm:text-base text-gray-500">
                      Loading more blogs... ({loadedCount}/{totalBlogs})
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="bg-white py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 md:mb-6">
              Ready to Start Your Next Project?
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-500 mb-6 sm:mb-8 leading-relaxed">
              Our development team is ready to help you build amazing web applications, mobile apps, 
              and AI-powered solutions. Let's discuss your project requirements.
            </p>
            <div className="flex gap-3 sm:gap-4 justify-center flex-wrap">
              <a
                href="/services"
                className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg text-sm sm:text-base md:text-lg font-semibold no-underline transition-colors inline-block"
              >
                View Our Services
              </a>
              <a
                href="/contact"
                className="bg-transparent hover:bg-[#00d690] text-[#00d690] hover:text-white px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg text-sm sm:text-base md:text-lg font-semibold border-2 border-[#00d690] no-underline transition-all inline-block"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Only on blog listing page */}
      <FAQ 
        faqs={blogFAQs}
        title="Blog FAQs"
        description="Common questions about our blog, topics, and content."
      />

      <Footer />
    </div>
  );
};

export default BlogListing;
